import { createPdfComposer } from "../Pdf/PdfComposer";
import { requestPdfExportAppearance } from "../Pdf/PdfExportAppearance";

/*
    🔥 EXPORTAR LA TABLA

    Cuatro salidas: CSV, Excel, PDF e impresión. Excel usa `xlsx`, que ya
    tenías instalado; las demás no agregan dependencias.

    ⚠️ SE EXPORTA LO QUE SE VE, NO TODO

    Si el usuario filtró y ordenó, el archivo sale filtrado y ordenado.
    Exportar el conjunto crudo cuando la pantalla muestra 12 filas es
    una sorpresa desagradable — y en tablas grandes, un archivo de
    50 MB que nadie pidió.

    Con `isServerSide` conviene que el backend genere el archivo: el
    navegador solo tiene la página actual.
*/

const onGetValue = (row, key) => (
    key.split('.').reduce((acc, part) => acc?.[part], row)
);

/*
    ⚠️ El valor exportado NO es el que se ve

    Una celda puede renderizar un <Badge> o un botón. En el archivo eso
    no sirve: se usa `exportValue` si la columna lo define, y si no, el
    dato crudo. Nunca el JSX.
*/
const onGetExportValue = (row, column) => {

    if (column.exportValue) return column.exportValue(row);

    const VALUE = onGetValue(row, column.key);

    if (VALUE === null || VALUE === undefined) return '';

    if (VALUE instanceof Date) return VALUE.toLocaleDateString('es-AR');

    if (typeof VALUE === 'boolean') return VALUE ? 'Sí' : 'No';

    if (typeof VALUE === 'object') return JSON.stringify(VALUE);

    return VALUE;
};

const onBuildMatrix = (columns, rows) => {

    const COLS = columns.filter((c) => c.isExportable !== false);

    const HEAD = COLS.map((c) => c.label);

    const BODY = rows.map((row) => COLS.map((col) => onGetExportValue(row, col)));

    return { head: HEAD, body: BODY, columns: COLS };
};

const onDownload = (blob, fileName) => {

    const URL_OBJ = URL.createObjectURL(blob);

    const LINK = document.createElement('a');
    LINK.href = URL_OBJ;
    LINK.download = fileName;
    LINK.click();

    // Sin esto la URL queda en memoria hasta cerrar la pestaña
    setTimeout(() => URL.revokeObjectURL(URL_OBJ), 1000);
};

/*
    🔥 CSV

    ⚠️ EL BOM AL PRINCIPIO NO ES UN ADORNO

    Excel en Windows abre el CSV en la codificación del sistema, no en
    UTF-8. Sin el BOM (\uFEFF), "Córdoba" se ve "CÃ³rdoba". Es el bug
    número uno de los CSV exportados desde el navegador.

    ⚠️ Y el separador es PUNTO Y COMA

    En configuración regional en español, Excel espera ';'. Con coma
    mete todo en una sola columna.
*/
export const onExportCsv = (columns, rows, fileName = 'export.csv', separator = ';') => {

    const { head, body } = onBuildMatrix(columns, rows);

    const onEscape = (value) => {

        const TEXT = String(value ?? '');

        // Si trae el separador, comillas o saltos, hay que entrecomillar
        if (TEXT.includes(separator) || TEXT.includes('"') || TEXT.includes('\n')) {
            return `"${TEXT.replace(/"/g, '""')}"`;
        }

        return TEXT;
    };

    const LINES = [head, ...body].map((line) => line.map(onEscape).join(separator));

    const CONTENT = `\uFEFF${LINES.join('\r\n')}`;

    onDownload(new Blob([CONTENT], { type: 'text/csv;charset=utf-8;' }), fileName);
};

/*
    🔥 EXCEL — con xlsx

    Se importa de forma dinámica: la librería pesa bastante y no tiene
    sentido cargarla en el bundle inicial si el usuario nunca exporta.
*/
export const onExportExcel = async (columns, rows, fileName = 'export.xlsx', sheetName = 'Datos') => {

    const XLSX = await import('xlsx');

    const { head, body, columns: COLS } = onBuildMatrix(columns, rows);

    const SHEET = XLSX.utils.aoa_to_sheet([head, ...body]);

    // Ancho de columna según el contenido más largo, con tope
    SHEET['!cols'] = COLS.map((col, i) => {

        const LONGEST = Math.max(
            String(head[i]).length,
            ...body.map((line) => String(line[i] ?? '').length),
        );

        return { wch: Math.min(Math.max(LONGEST + 2, 10), 50) };
    });

    const BOOK = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(BOOK, SHEET, sheetName);

    XLSX.writeFile(BOOK, fileName);
};

/*
    🔥 IMPRIMIR

    ⚠️ Se abre una ventana nueva con SOLO la tabla en HTML plano.

    Imprimir la página entera arrastraría el sidebar, el header y el
    panel de desarrollo. Y usar @media print sobre el DOM real obliga
    a esconder media app con CSS, que se rompe en cuanto agregás algo.
*/
export const onPrintTable = (columns, rows, title = 'Listado') => {

    const { head, body } = onBuildMatrix(columns, rows);

    const HTML = `
        <!doctype html>
        <html lang="es">
        <head>
            <meta charset="utf-8">
            <title>${title}</title>
            <style>
                * { box-sizing: border-box; }
                body { font-family: system-ui, sans-serif; padding: 28px; color: #1a1a1a; }
                h1 { font-size: 18px; margin: 0 0 4px; }
                .meta { color: #666; font-size: 12px; margin-bottom: 18px; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                th { background: #f2f2f2; text-align: left; }
                th, td { padding: 7px 9px; border: 1px solid #ddd; }
                tr:nth-child(even) td { background: #fafafa; }
                @page { margin: 14mm; }
                /* La cabecera se repite en cada hoja impresa */
                thead { display: table-header-group; }
            </style>
        </head>
        <body>
            <h1>${title}</h1>
            <p class="meta">${rows.length} registros · ${new Date().toLocaleString('es-AR')}</p>
            <table>
                <thead><tr>${head.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
                <tbody>
                    ${body.map((line) => `<tr>${line.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}
                </tbody>
            </table>
        </body>
        </html>
    `;

    const WIN = window.open('', '_blank');

    if (!WIN) {
        console.warn('[Table] El navegador bloqueó la ventana de impresión.');
        return;
    }

    WIN.document.write(HTML);
    WIN.document.close();

    // Esperamos a que renderice antes de llamar a print
    WIN.onload = () => { WIN.focus(); WIN.print(); };
};

/*
    NOTEBOOK PDF — exportación local con apariencia configurable.
    - Título principal en negrita + subrayado.
    - Nombre de campo en negrita.
    - Marca NOTEBOOK + dominio de origen en encabezado/pie.
    - Etiqueta semántica configurable: Registro, Paso, Tarjeta, etc.
*/
const onPdfBytes = (title, columns, rows, appearance, options = {}) => {
    const { head, body } = onBuildMatrix(columns, rows);
    const recordLabel = String(options.recordLabel || "REGISTRO").trim().toUpperCase();
    const plural = String(options.recordLabelPlural || `${recordLabel.toLowerCase()}s`).trim();
    const pdf = createPdfComposer({ appearance, documentTitle: title || "Listado" });

    pdf.addTitle(title || "Listado");
    pdf.addMeta(`${rows.length} ${plural} · ${new Date().toLocaleString("es-AR")}`);
    pdf.addRule({ after: 9 });

    body.forEach((line, index) => {
        pdf.addSectionTitle(`${recordLabel} ${index + 1}`);
        line.forEach((value, colIndex) => pdf.addLabelValue(head[colIndex], value));
        pdf.addSpacer(7);
    });

    return pdf.finish();
};

export const onExportPdf = async (columns, rows, fileName = "export.pdf", title = "Listado", options = {}) => {
    const appearance = options.appearance || await requestPdfExportAppearance({ title });
    if (!appearance) return false;
    const bytes = onPdfBytes(title, columns, rows, appearance, options);
    onDownload(new Blob([bytes], { type: "application/pdf" }), fileName);
    return true;
};
